import { Box, FormHelperText, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, { useContext, useEffect, useState } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { StoreFileInput } from '../../../__generated__/pgeplus-types';
import useFileUploadApi from '../../../hooks/pge-plus/useFileUploadApi';
import useContentMgmt from '../../../hooks/useContentMgmt';
import { FormState } from '../../../hooks/useFormState.types';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import colors from '../../../themes/main-colors';
import Backdrop from '../../backdrop';
import theme from '../../../themes/theme';
import microcopyGroupIds from '../microcopyGroupIds';
import UploadIcon from '../static/uploadIcon.svg';
import { Progress } from './Progress';
import { FILE_TYPE_PDF, getSupportedFileNames } from '../FileUploader';
import imageCompression from 'browser-image-compression';

type TMultiUploadProps = {
  maxFiles?: number;
  maxSize?: number;
  acceptMimeTypes?: Record<string, []>;
  dropZoneStyles?: React.CSSProperties;
  form?: FormState<any>;
  formField?: string;
  setIsFileUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  uploadText?: React.ReactNode;
};

type TFileWithStoreKey = File & {
  pgeStoreKey?: string;
};

export enum DraggedItemStatus {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

const DEFAULT_MAX_FILE_SIZE_COMPRESSED = 1024 * 1024 * 4.5; //6MB

//Users will be able to pick image files upto 30 MB, but should be within 6MB after compression.
const DEFAULT_MAX_FILE_SIZE_IN_MB = 25; //30MB.

const DEFAULT_PARAMS: TMultiUploadProps = {
  maxFiles: 4, // C2M only accepts a max of 4 receipt images. If more is required, a C2M update is needed.
  maxSize: 1024 * 1024 * DEFAULT_MAX_FILE_SIZE_IN_MB, //Size in Bytes
  acceptMimeTypes: {
    'image/png': [],
    'image/jpeg': [],
    'application/pdf': [],
  },
};

export const MultiFileUpload = (props: TMultiUploadProps) => {
  const {
    maxFiles = DEFAULT_PARAMS.maxFiles,
    acceptMimeTypes: accept = DEFAULT_PARAMS.acceptMimeTypes,
    maxSize = DEFAULT_PARAMS.maxSize,
    dropZoneStyles,
    form,
    formField = '',
    setIsFileUploading,
    uploadText,
  } = props;

  const {
    content,
    richText,
    inlineRichText,
    isContentLoading,
  } = useContentMgmt(microcopyGroupIds.PGE_PLUS_REBATE_ONLY_MULTI_UPLOAD);
  const notificationContext = useContext(NotificationsContext);
  const { uploadFile } = useFileUploadApi();
  const [loading, setLoading] = useState<boolean>(false);

  const [files, setFiles] = useState<
    Array<StoreFileInput | TFileWithStoreKey | FileRejection>
  >([]);
  const [maxFilesError, setMaxFilesError] = useState<boolean>(false);
  const [isCompressing, setIsCompressing] = useState(false);

  useEffect(() => {
    setIsFileUploading?.(loading);
  }, [loading]);

  useEffect(() => {
    if (form?.props(formField)?.value?.length) {
      setFiles(form?.props(formField)?.value);
    }
  }, [form?.props(formField)?.value]);

  const setFormValue = async (file: TFileWithStoreKey) => {
    const prevFormValues = form?.props(formField)?.value || [];
    const storeFile: StoreFileInput = {
      id: file?.pgeStoreKey || '',
      name: file?.name,
      size: String(file?.size),
    };
    const values: StoreFileInput[] = [...prevFormValues, storeFile];
    await form?.setValue(formField, values);
  };

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    if (loading) {
      return;
    }
    const formValues = form?.props(formField)?.value as StoreFileInput[];
    if (
      (files?.length || 0) + acceptedFiles.length + fileRejections.length <=
      (maxFiles ?? DEFAULT_PARAMS.maxFiles!)
    ) {
      const validFiles: File[] = [];

      for (const file of acceptedFiles) {
        let proccessedFile = file;
        if (
          file.type !== FILE_TYPE_PDF &&
          file.size > DEFAULT_MAX_FILE_SIZE_COMPRESSED
        ) {
          try {
            setIsCompressing(true);
            proccessedFile = await imageCompression(file, {
              maxSizeMB: 1,
              useWebWorker: true,
              initialQuality: 0.6,
            });
          } catch (error) {
            fileRejections.push({
              file,
              errors: [{ code: 'compression-failed', message: '' }],
            });
            continue;
          }
        }

        if (proccessedFile.size <= DEFAULT_MAX_FILE_SIZE_COMPRESSED) {
          validFiles.push(proccessedFile);
        } else {
          fileRejections.push({
            file,
            errors: [
              {
                code: 'compression-exceeds-file-limit',
                message: content.get('FILE_SIZE_EXCEEDS_THE_LIMIT') || '',
              },
            ],
          });
        }
      }
      setIsCompressing(false);
      setMaxFilesError(false);
      const acceptedFilesWithStoreKeys: TFileWithStoreKey[] = validFiles;
      const numberOfFilesToUpload = acceptedFilesWithStoreKeys.length;
      const totalExpectedFiles =
        (formValues?.length || 0) + numberOfFilesToUpload;
      acceptedFilesWithStoreKeys?.forEach(async file => {
        setLoading(true);
        const response = await uploadFile(file);
        if (response?.success && response?.pgeStoreKey) {
          file.pgeStoreKey = response.pgeStoreKey;
          await setFormValue(file);
          if (totalExpectedFiles === form?.props(formField)?.value?.length) {
            setLoading(false);
          }
        } else {
          setLoading(false);
          notificationContext.setState({
            isOpen: true,
            severity: 'error',
            variant: 'filled',
            message: content.get('SOMETHING_WENT_WRONG'),
          });
          window.scrollTo(0, 0);
        }
      });
      setFiles(prev => [
        ...(prev || []),
        ...acceptedFilesWithStoreKeys,
        ...fileRejections,
      ]);
    } else {
      setMaxFilesError(true);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    multiple: true,
    maxSize,
  });

  const handleRemoveClick = async (
    file: StoreFileInput | TFileWithStoreKey | FileRejection,
  ) => {
    const formValues = form?.props(formField)?.value as StoreFileInput[];
    const filteredFormValues = formValues?.filter(
      val =>
        val?.name !== (file as StoreFileInput)?.name &&
        (val?.size
          ? String(val?.size) !== (file as StoreFileInput)?.size
          : true) &&
        val?.id !== (file as StoreFileInput)?.id,
    );
    await form?.setValue(formField, filteredFormValues);
    const filteredDraggedValues = files?.filter(draggedFile => {
      if ('errors' in draggedFile) {
        const { file: rejectedFile } = draggedFile;
        return (
          rejectedFile?.name !== (file as StoreFileInput)?.name &&
          String(rejectedFile?.size) !== (file as StoreFileInput)?.size
        );
      } else {
        return (
          draggedFile?.name !== (file as StoreFileInput)?.name &&
          String(draggedFile?.size) !== (file as StoreFileInput)?.size
        );
      }
    });
    setFiles(filteredDraggedValues);
  };

  const isAlreadyUploaded = (file: TFileWithStoreKey) =>
    form
      ?.props(formField)
      ?.value?.some(
        (existingFile: StoreFileInput) =>
          existingFile?.name === file?.name &&
          existingFile?.size === String(file?.size),
      );

  const renderFileListItem = (
    file: StoreFileInput | File,
    status: DraggedItemStatus,
    fileAlreadyUploaded: boolean,
  ) => (
    <Box
      css={{
        background: colors.white,
        display: 'flex',
        borderRadius: '5px',
        border: `1px solid
            ${
              status === DraggedItemStatus.ACCEPTED
                ? colors.inputFieldBackground
                : colors.errorRed
            }`,
        padding: '12px',
        ...(status === DraggedItemStatus.ACCEPTED && {
          marginBottom: '16px',
        }),
      }}
    >
      <Typography
        css={{
          color: colors.noirBlur,
          fontWeight: 'normal',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        variant="subtitle2"
        component={'span'}
      >
        {file?.name}
      </Typography>
      {Boolean(file?.size) && (
        <Typography
          css={{
            display: 'block',
            color: colors.darkGray3,
            fontSize: '12px',
            fontWeight: 'normal',
            marginLeft: '10px',
            marginRight: '10px',
            paddingTop: '2px',
            [theme.breakpoints.down('md')]: {
              paddingTop: '2px',
            },
          }}
          variant="subtitle2"
          component={'span'}
        >
          {`${Math.round(Number(file?.size || 0) / 1024)}kB`}
        </Typography>
      )}
      <Box
        css={{ marginLeft: 'auto', cursor: 'pointer', marginRight: '5px' }}
        onClick={() => handleRemoveClick(file)}
      >
        {(status === DraggedItemStatus.REJECTED ||
          !loading ||
          fileAlreadyUploaded) && <CloseIcon />}
      </Box>
      <Box>
        <Progress
          loading={loading}
          fileAlreadyUploaded={fileAlreadyUploaded}
          status={status}
        />
      </Box>
    </Box>
  );

  if (isContentLoading) {
    return <Backdrop forceOpen />;
  }

  return (
    <Box>
      <Box
        {...getRootProps()}
        css={{
          height: '200px',
          borderRadius: '5px',
          cursor: 'pointer',
          border: `2px dashed ${colors.sparkBlue}`,
          backgroundColor: colors.lightBlue1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        style={dropZoneStyles}
      >
        <input {...getInputProps()} />
        {!isDragActive &&
          (uploadText ? (
            uploadText
          ) : (
            <Box
              style={{
                fontSize: 16,
                fontWeight: 'bold',
                lineHeight: '20px',
                color: colors.sparkBlue,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <UploadIcon
                fontSize="large"
                css={{ verticalAlign: 'middle', display: 'block' }}
              />
              <Typography
                css={{
                  display: 'block',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}
                variant="subtitle1"
                component={'p'}
              >
                {richText(content.get('DRAG_AND_DROP_FILES_TO_UPLOAD'))}
              </Typography>
              <Typography
                css={{
                  display: 'block',
                  textAlign: 'center',
                  color: colors.darkGray3,
                  fontSize: '12px',
                  fontWeight: 'normal',
                }}
                variant="subtitle2"
                component={'p'}
              >
                {content.get('SUPPORTED_FILES_TYPES')}
              </Typography>
            </Box>
          ))}
      </Box>
      {maxFilesError && (
        <FormHelperText error>{content.get('MAX_FILES_ERROR')}</FormHelperText>
      )}
      <Box
        css={{
          marginTop: '16px',
        }}
      >
        {files?.map(file => {
          if ('id' in file) {
            return renderFileListItem(file, DraggedItemStatus.ACCEPTED, true);
          }
          if ('errors' in file) {
            return (
              <Box
                key={file.file?.lastModified + file.file?.name}
                css={{
                  marginBottom: '16px',
                }}
              >
                {renderFileListItem(
                  file.file,
                  DraggedItemStatus.REJECTED,
                  false,
                )}
                {file?.errors?.map(err => {
                  let errorMessage: React.ReactNode = '';
                  if (err.code === ErrorCode.FileInvalidType) {
                    errorMessage = inlineRichText(
                      content.get('INCORRECT_FILE_TYPE') || '',
                      {
                        SUPPORTED_FILE_TYPES: getSupportedFileNames(
                          Object.keys(accept!),
                        ),
                      },
                    );
                  }
                  if (err.code === ErrorCode.FileTooLarge) {
                    errorMessage =
                      content.get('MAX_ALLOWED_PGE_PLUS_FILE_LIMIT') || '';
                  }
                  if (err.code === ErrorCode.TooManyFiles) {
                    errorMessage = content.get('MAX_FILES_ERROR') || '';
                  }
                  if (err.code === 'compression-failed') {
                    errorMessage =
                      content.get('IMAGE_COMPRESSION_FAILED') || '';
                  }
                  if (err.code === 'compression-exceeds-file-limit') {
                    errorMessage =
                      content.get('FILE_SIZE_EXCEEDS_THE_LIMIT') || '';
                  }
                  return <FormHelperText error>{errorMessage}</FormHelperText>;
                })}
              </Box>
            );
          }
          return renderFileListItem(
            file,
            DraggedItemStatus.ACCEPTED,
            isAlreadyUploaded(file),
          );
        })}
      </Box>
      {isCompressing && (
        <Box
          css={{
            marginTop: '16px',
          }}
        >
          {content.get('PROCESSING_FILE_PLEASE_WAIT')}
        </Box>
      )}
    </Box>
  );
};
